// ============================================================================
// MÓDULO 7 - UNIDAD 2: ROUTINE AND FREE TIME
// ============================================================================
// Nivel: B1-B2 (Upper-Intermediate)
// Gramática: Adverb placement (frequency adverbs)
// Vocabulario: Frequency scale, leisure activities
// Contexto: Daily routines & free time activities
// ============================================================================

export const M7_UNIT_2 = {
  id: "m7-u2",
  title: "Unit 2: Routine and Free Time",
  topic: "Master adverb placement, frequency expressions, and talk about daily routines and leisure activities naturally!",
  
  grammarImage: "/images/grammar/m7-u2-adverbs.jpg",
  grammarText: `
    <h3>Adverbs of Frequency - Position Rules</h3>
    
    <div class="grammar-rule">
      <h4>GENERAL RULE: Before Main Verb</h4>
      <p>Adverbs go BETWEEN subject and main verb:</p>
      <ul>
        <li>✅ I <strong>usually</strong> take a shower in the morning</li>
        <li>✅ She <strong>always</strong> checks her email first</li>
        <li>✅ They <strong>rarely</strong> eat breakfast at home</li>
        <li>❌ I take usually a shower (INCORRECT)</li>
      </ul>
      <p><strong>Pattern:</strong> Subject + ADVERB + Verb</p>
    </div>

    <div class="grammar-rule exception">
      <h4>EXCEPTION: After Verb "BE"</h4>
      <p>With the verb "be", adverb comes AFTER:</p>
      <ul>
        <li>✅ The traffic <strong>is often</strong> bad in the morning</li>
        <li>✅ She <strong>is always</strong> on time for meetings</li>
        <li>✅ They <strong>are rarely</strong> late</li>
        <li>❌ The traffic often is bad (INCORRECT)</li>
        <li>❌ She always is on time (INCORRECT)</li>
      </ul>
      <p><strong>Pattern:</strong> Subject + BE + ADVERB</p>
    </div>

    <div class="frequency-scale">
      <h4>Frequency Scale (0% → 100%)</h4>
      <p><strong>Never</strong> (0%) → <strong>Rarely/Seldom</strong> → <strong>Occasionally/Sometimes</strong> → <strong>Often/Frequently</strong> → <strong>Usually</strong> → <strong>Always</strong> (100%)</p>
    </div>

    <div class="common-errors">
      <h4>Common Errors to Avoid:</h4>
      <ul>
        <li>❌ I walk always to work → ✅ I always walk to work</li>
        <li>❌ She often is late → ✅ She is often late</li>
        <li>❌ They go never out → ✅ They never go out</li>
      </ul>
    </div>

    <div class="end-position">
      <h4>Frequency Expressions at END of Sentence</h4>
      <p>Some expressions go at the END:</p>
      <ul>
        <li>✅ I exercise <strong>three times a week</strong></li>
        <li>✅ We meet <strong>once a month</strong></li>
        <li>✅ She travels <strong>twice a year</strong></li>
      </ul>
    </div>
  `,

  iconType: "clock",

  sets: [
    // ========================================================================
    // SET 1: ADVERB PLACEMENT - BEFORE MAIN VERB
    // ========================================================================
    {
      id: "m7-u2-s1",
      title: "Set 1: Adverb Placement with Main Verbs",
      questions: [
        {
          type: "mc",
          q: "Which sentence has CORRECT adverb placement?",
          options: [
            "I usually wake up at 6:30 AM",
            "I wake up usually at 6:30 AM",
            "Usually I wake up at 6:30 AM"
          ],
          a: "I usually wake up at 6:30 AM",
          tip: "📍 ADVERB POSITION!\n\nWith main verbs (not 'be'):\n✅ Subject + ADVERB + Verb\n'I usually wake up'\n\n'Usually I wake up' = grammatically OK but less common in neutral statements.\n\nThe most natural position is BETWEEN subject and verb! 🎯",
          successtip: "✓ PERFECT PLACEMENT!\nSubject + adverb + verb = correct! ⏰\nI usually wake up ✅"
        },
        {
          type: "mc",
          q: "Choose the correct sentence:",
          options: [
            "She always drinks coffee in the morning",
            "She drinks always coffee in the morning",
            "Always she drinks coffee in the morning"
          ],
          a: "She always drinks coffee in the morning",
          successtip: "✓ CORRECT ORDER!\nShe + always + drinks = perfect! ☕\nAdverb before main verb!"
        },
        {
          type: "blank",
          q: "I ___ check my phone before breakfast. (rarely)",
          a: "rarely",
          accept: { ignoreCase: true },
          successtip: "✓ ADVERB INSERTED!\nI rarely check = correct position 📱\nBetween subject and verb!"
        },
        {
          type: "mc",
          q: "Which is INCORRECT?",
          options: [
            "He goes often to the gym",
            "He often goes to the gym",
            "Often, he goes to the gym"
          ],
          a: "He goes often to the gym",
          tip: "🚫 COMMON ERROR!\n\n❌ 'He goes often' = WRONG position\n✅ 'He often goes' = CORRECT\n✅ 'Often, he goes' = also OK (emphasis)\n\nThe adverb must come BEFORE the main verb, not after!\n\nThink: Subject + ADVERB + Verb 🎯",
          successtip: "✓ ERROR SPOTTED!\n'Goes often' = incorrect ❌\n'Often goes' = correct ✅"
        },
        {
          type: "order",
          q: "Traduce: Ellos frecuentemente caminan al trabajo juntos",
          a: "They frequently walk to work together",
          successtip: "✓ PERFECT SENTENCE!\nThey + frequently + walk = correct order 🚶\nAdverb before verb!"
        },
        {
          type: "mc",
          q: "Complete: 'We ___ our weekends relaxing at home.'",
          options: [
            "usually spend",
            "spend usually",
            "are usually spending"
          ],
          a: "usually spend",
          successtip: "✓ NATURAL FLOW!\n'Usually spend' = correct placement 🏠\nAdverb before main verb!"
        },
        {
          type: "blank",
          q: "My sister ___ eats lunch at her desk. (occasionally)",
          a: "occasionally",
          accept: { ignoreCase: true },
          successtip: "✓ OCCASIONAL HABIT!\nSister + occasionally + eats = correct! 🍱\nMid-frequency adverb placed perfectly!"
        },
        {
          type: "mc",
          q: "Which shows INCORRECT adverb placement?",
          options: [
            "I take sometimes a nap in the afternoon",
            "I sometimes take a nap in the afternoon",
            "Sometimes I take a nap in the afternoon"
          ],
          a: "I take sometimes a nap in the afternoon",
          successtip: "✓ ERROR IDENTIFIED!\n'Take sometimes' = wrong ❌\n'Sometimes take' = correct ✅"
        },
        {
          type: "order",
          q: "Traduce: Ella raramente cocina durante la semana",
          a: "She rarely cooks during the week",
          successtip: "✓ PERFECT STRUCTURE!\nShe + rarely + cooks = correct! 🍳\nLow frequency, perfect placement!"
        },
        {
          type: "mc",
          q: "Choose the most natural sentence:",
          options: [
            "They always arrive early for meetings",
            "They arrive always early for meetings",
            "Always they arrive early for meetings"
          ],
          a: "They always arrive early for meetings",
          successtip: "✓ MOST NATURAL!\nThey + always + arrive = perfect! 📅\nStandard English word order!"
        }
      ]
    },

    // ========================================================================
    // SET 2: MORE PRACTICE - MAIN VERB PLACEMENT
    // ========================================================================
    {
      id: "m7-u2-s2",
      title: "Set 2: Mastering Adverb Position",
      questions: [
        {
          type: "mc",
          q: "Which sentence is grammatically correct?",
          options: [
            "I never eat fast food",
            "I eat never fast food",
            "Never I eat fast food"
          ],
          a: "I never eat fast food",
          tip: "📍 'NEVER' PLACEMENT!\n\n'Never' follows the same rule:\n✅ Subject + NEVER + Verb\n'I never eat'\n\n❌ 'I eat never' = incorrect\n\n'Never' is a frequency adverb (0%), so it goes before the main verb! 🎯",
          successtip: "✓ CORRECT NEVER!\nI + never + eat = perfect! 🚫🍔\nZero frequency, correct placement!"
        },
        {
          type: "blank",
          q: "He ___ forgets his wife's birthday. (never)",
          a: "never",
          accept: { ignoreCase: true },
          successtip: "✓ NEVER FORGETS!\nHe + never + forgets = reliable! 🎂\nPerfect placement!"
        },
        {
          type: "mc",
          q: "Complete correctly: 'My colleagues ___ lunch together in the cafeteria.'",
          options: [
            "often have",
            "have often",
            "are often having"
          ],
          a: "often have",
          successtip: "✓ NATURAL HABIT!\n'Often have' = correct! 🍽️\nAdverb before main verb!"
        },
        {
          type: "order",
          q: "Traduce: Nosotros usualmente nos reunimos los viernes por la tarde",
          a: "We usually meet on Friday afternoons",
          successtip: "✓ WEEKLY ROUTINE!\nWe + usually + meet = perfect! 📅\nHigh frequency pattern!"
        },
        {
          type: "mc",
          q: "Which is the WRONG way to say it?",
          options: [
            "She listens frequently to podcasts",
            "She frequently listens to podcasts",
            "Frequently, she listens to podcasts"
          ],
          a: "She listens frequently to podcasts",
          tip: "🚫 AFTER-VERB ERROR!\n\n❌ 'Listens frequently' = WRONG\n✅ 'Frequently listens' = CORRECT\n✅ 'Frequently, she...' = also OK\n\nAdverb of frequency CANNOT come after the main verb!\n\nAlways before: Subject + ADVERB + Verb 📻",
          successtip: "✓ ERROR CAUGHT!\n'Listens frequently' = incorrect ❌\nAdverb must come BEFORE verb!"
        },
        {
          type: "blank",
          q: "They ___ go jogging before work. (frequently)",
          a: "frequently",
          accept: { ignoreCase: true, alts: ["often"] },
          successtip: "✓ MORNING ROUTINE!\nThey + frequently + go = healthy habit! 🏃\nCorrect placement!"
        },
        {
          type: "mc",
          q: "Choose the correct sentence:",
          options: [
            "I always remember to call my parents on Sundays",
            "I remember always to call my parents on Sundays",
            "Always I remember to call my parents on Sundays"
          ],
          a: "I always remember to call my parents on Sundays",
          successtip: "✓ SWEET HABIT!\nI + always + remember = perfect! 📞❤️\nCorrect word order!"
        },
        {
          type: "mc",
          q: "Which shows proper adverb placement?",
          options: [
            "We occasionally try new restaurants",
            "We try occasionally new restaurants",
            "Occasionally we not try new restaurants"
          ],
          a: "We occasionally try new restaurants",
          successtip: "✓ ADVENTUROUS!\nWe + occasionally + try = correct! 🍽️✨\nMid-frequency, perfect position!"
        },
        {
          type: "order",
          q: "Traduce: Él siempre revisa su correo electrónico antes de las reuniones",
          a: "He always checks his email before meetings",
          successtip: "✓ PROFESSIONAL HABIT!\nHe + always + checks = organized! 📧\n100% frequency!"
        },
        {
          type: "mc",
          q: "Final question: 'My manager ___ us detailed feedback on our projects.' Which is most natural?",
          options: [
            "usually gives",
            "gives usually",
            "is usually giving"
          ],
          a: "usually gives",
          successtip: "✓ WORK ROUTINE!\n'Usually gives' = natural feedback pattern! 💼\nAdverb perfectly placed!"
        }
      ]
    },

    // ========================================================================
    // SET 3: EXCEPTION WITH VERB "BE"
    // ========================================================================
    {
      id: "m7-u2-s3",
      title: "Set 3: Exception - Adverbs with Verb 'BE'",
      questions: [
        {
          type: "mc",
          q: "Which sentence is CORRECT with the verb 'be'?",
          options: [
            "The traffic is usually heavy in the morning",
            "The traffic usually is heavy in the morning",
            "Usually the traffic is heavy in the morning"
          ],
          a: "The traffic is usually heavy in the morning",
          tip: "⚠️ IMPORTANT EXCEPTION!\n\nWith verb 'BE', adverb comes AFTER:\n✅ Subject + BE + ADVERB\n'The traffic IS usually heavy'\n\n❌ 'Usually is' = WRONG\n\nThis is THE exception to the general rule!\n\nMain verbs: adverb BEFORE\nVerb 'be': adverb AFTER 🎯",
          successtip: "✓ BE EXCEPTION!\nIS + usually = correct! 🚗\nAdverb AFTER 'be'!"
        },
        {
          type: "mc",
          q: "Choose the correct sentence:",
          options: [
            "She is always punctual",
            "She always is punctual",
            "Always she is punctual"
          ],
          a: "She is always punctual",
          successtip: "✓ AFTER 'BE'!\nIs + always = correct! ⏰\nException rule applied!"
        },
        {
          type: "blank",
          q: "My coffee ___ hot when I drink it in the car. (rarely)",
          a: "is rarely",
          accept: { ignoreCase: true, alts: ["'s rarely"] },
          successtip: "✓ BE + ADVERB!\nIs rarely hot = correct order! ☕\nException rule mastered!"
        },
        {
          type: "mc",
          q: "Which is INCORRECT?",
          options: [
            "They often are late for class",
            "They are often late for class",
            "Often, they are late for class"
          ],
          a: "They often are late for class",
          tip: "🚫 'OFTEN ARE' ERROR!\n\n❌ 'Often are' = WRONG order with 'be'\n✅ 'Are often' = CORRECT\n\nWith 'be', the adverb must come AFTER:\nSubject + BE + ADVERB\n\nThis is a very common mistake! 🎯",
          successtip: "✓ ERROR FOUND!\n'Often are' = wrong ❌\n'Are often' = correct ✅"
        },
        {
          type: "order",
          q: "Traduce: El clima es frecuentemente impredecible en primavera",
          a: "The weather is frequently unpredictable in spring",
          successtip: "✓ WEATHER PATTERN!\nIs + frequently = correct! 🌦️\nBE verb exception applied!"
        },
        {
          type: "mc",
          q: "Complete: 'My boss ___ in a good mood on Mondays.'",
          options: [
            "is rarely",
            "rarely is",
            "is rare"
          ],
          a: "is rarely",
          successtip: "✓ MONDAY MOOD!\nIs + rarely = correct structure! 😅\nADVERB after BE!"
        },
        {
          type: "blank",
          q: "The gym ___ crowded in the early morning. (never)",
          a: "is never",
          accept: { ignoreCase: true, alts: ["'s never"] },
          successtip: "✓ EMPTY GYM!\nIs never crowded = perfect! 🏋️\nBE + never = correct!"
        },
        {
          type: "mc",
          q: "Which shows CORRECT placement with 'be'?",
          options: [
            "The meetings are usually productive",
            "The meetings usually are productive",
            "Usually the meetings are productive"
          ],
          a: "The meetings are usually productive",
          successtip: "✓ MEETING QUALITY!\nAre + usually = correct! 💼\nException rule perfect!"
        },
        {
          type: "order",
          q: "Traduce: Mis estudiantes son siempre entusiastas durante las discusiones",
          a: "My students are always enthusiastic during discussions",
          successtip: "✓ ENGAGED CLASS!\nAre + always = correct! 🎓\nBE verb + adverb = perfect!"
        },
        {
          type: "mc",
          q: "Final check: 'The subway ___ on time during rush hour.' Which is correct?",
          options: [
            "is rarely",
            "rarely is",
            "rare is"
          ],
          a: "is rarely",
          tip: "🚇 BE + RARELY!\n\n✅ 'Is rarely' = correct\n'The subway IS RARELY on time'\n\nRemember the pattern with 'be':\nSubject + BE + ADVERB + complement\n\nThis exception is crucial for natural English! 🎯",
          successtip: "✓ TRANSPORT REALITY!\nIs + rarely = correct! 🚇\nBE exception mastered!"
        }
      ]
    },

    // ========================================================================
    // SET 4: CONTRASTING BE VS MAIN VERBS
    // ========================================================================
    {
      id: "m7-u2-s4",
      title: "Set 4: Contrasting BE and Main Verbs",
      questions: [
        {
          type: "mc",
          q: "Both sentences are correct. Identify which rule each follows: 'She is always happy' vs 'She always smiles'",
          options: [
            "First uses BE (adverb after), second uses main verb (adverb before)",
            "Both use the same rule",
            "Both are incorrect"
          ],
          a: "First uses BE (adverb after), second uses main verb (adverb before)",
          tip: "🎯 TWO DIFFERENT RULES!\n\n'She IS always happy' → BE + adverb\n'She always SMILES' → adverb + verb\n\nDifferent verbs, different positions!\n\nBE = exception (adverb after)\nMain verbs = rule (adverb before)\n\nYou need to recognize the verb type! 📚",
          successtip: "✓ RULES COMPARED!\nBE → adverb after ✅\nMain verb → adverb before ✅\nYou understand both!"
        },
        {
          type: "blank",
          q: "He ___ late, but he ___ arrives before the meeting starts. (is often / always)\n\nAnswer with both adverbs in order:",
          a: "is often, always",
          accept: { ignoreCase: true, alts: ["is often / always", "is often, always"] },
          successtip: "✓ BOTH RULES!\nIs often (BE) + always arrives (main verb) 🎯\nBoth patterns in one sentence!"
        },
        {
          type: "mc",
          q: "Which sentence uses BOTH patterns correctly?",
          options: [
            "I am usually tired after work, but I always exercise anyway",
            "I usually am tired after work, but I exercise always anyway",
            "I am usually tired after work, but I exercise always anyway"
          ],
          a: "I am usually tired after work, but I always exercise anyway",
          successtip: "✓ DOUBLE MASTERY!\nAm usually (BE) + always exercise (main) 💪\nBoth rules applied perfectly!"
        },
        {
          type: "order",
          q: "Traduce: Ella está frecuentemente ocupada pero raramente se queja",
          a: "She is frequently busy but rarely complains",
          successtip: "✓ TWO PATTERNS!\nIs frequently (BE) + rarely complains (main) 🌟\nBoth rules in one sentence!"
        },
        {
          type: "mc",
          q: "Identify the error: 'The coffee usually is cold when I drink it, so I never drink it slowly.'",
          options: [
            "'Usually is' should be 'is usually'",
            "'Never drink' should be 'drink never'",
            "Both are wrong"
          ],
          a: "'Usually is' should be 'is usually'",
          tip: "🚫 BE VERB ERROR!\n\n❌ 'Usually is' = WRONG with BE\n✅ 'Is usually' = CORRECT\n\n'Never drink' = CORRECT (main verb)\n\nThe error is only with the BE verb!\n\nBE is the exception: adverb comes AFTER! ☕",
          successtip: "✓ ERROR IDENTIFIED!\n'Usually is' = wrong ❌\nShould be 'is usually' ✅"
        },
        {
          type: "mc",
          q: "Choose the sentence with correct adverb placement in BOTH clauses:",
          options: [
            "My schedule is always full, and I rarely have free time",
            "My schedule always is full, and I have rarely free time",
            "My schedule is always full, and I have rarely free time"
          ],
          a: "My schedule is always full, and I rarely have free time",
          successtip: "✓ BOTH CORRECT!\nIs always (BE) + rarely have (main) 📅\nPerfect understanding!"
        },
        {
          type: "blank",
          q: "Complete both blanks: 'Traffic ___ bad in the morning, so I ___ leave early.' (is often / usually)",
          a: "is often, usually",
          accept: { ignoreCase: true, alts: ["is often / usually", "is often, usually"] },
          successtip: "✓ CAUSE AND EFFECT!\nIs often (BE) + usually leave (main) 🚗\nBoth patterns mastered!"
        },
        {
          type: "mc",
          q: "Which uses adverb placement correctly throughout?",
          options: [
            "She is never late, and she always prepares well for presentations",
            "She never is late, and she always prepares well for presentations",
            "She is never late, and she prepares always well for presentations"
          ],
          a: "She is never late, and she always prepares well for presentations",
          successtip: "✓ PROFESSIONAL HABITS!\nIs never (BE) + always prepares (main) 💼\nBoth rules applied perfectly!"
        },
        {
          type: "order",
          q: "Traduce: El café siempre está caliente pero yo raramente lo bebo rápido",
          a: "The coffee is always hot but I rarely drink it quickly",
          successtip: "✓ COMPLEX SENTENCE!\nIs always (BE) + rarely drink (main) ☕\nBoth patterns in contrast!"
        },
        {
          type: "mc",
          q: "Final mastery: 'The presentations ___ interesting, and speakers ___ well-prepared.' Choose correct adverbs placement:",
          options: [
            "are usually / are always",
            "usually are / always are",
            "are usually / always are"
          ],
          a: "are usually / are always",
          successtip: "✓ BOTH WITH BE! 🎯\nAre usually + are always = both correct!\nBoth use BE verb exception rule!"
        }
      ]
    },

    // ========================================================================
    // SET 5: FREQUENCY SCALE VOCABULARY
    // ========================================================================
    {
      id: "m7-u2-s5",
      title: "Set 5: Frequency Scale - Always to Never",
      questions: [
        {
          type: "mc",
          q: "Which frequency adverb means 100% of the time?",
          options: ["Always", "Usually", "Often"],
          a: "Always",
          tip: "📊 FREQUENCY SCALE!\n\n100% = ALWAYS (every single time)\n90% = USUALLY (most of the time)\n70% = OFTEN/FREQUENTLY (many times)\n50% = SOMETIMES/OCCASIONALLY (some times)\n30% = RARELY/SELDOM (not many times)\n0% = NEVER (not at any time)\n\nKnowing the scale helps you be precise! 🎯",
          successtip: "✓ ABSOLUTE FREQUENCY!\nAlways = 100% 💯\nEvery single time!"
        },
        {
          type: "mc",
          q: "'Rarely' means approximately:",
          options: [
            "10-20% of the time",
            "50% of the time",
            "80-90% of the time"
          ],
          a: "10-20% of the time",
          successtip: "✓ LOW FREQUENCY!\nRarely = very infrequent 📉\nAround 10-20% only!"
        },
        {
          type: "blank",
          q: "If something happens about 50% of the time, you would say it ___ happens. (sometimes/occasionally)",
          a: "sometimes",
          accept: { ignoreCase: true, alts: ["occasionally"] },
          successtip: "✓ MID-FREQUENCY!\nSometimes/occasionally = about half the time ⚖️\n~50% frequency!"
        },
        {
          type: "mc",
          q: "Choose the adverb that means 'almost always, but not 100%':",
          options: ["Usually", "Rarely", "Never"],
          a: "Usually",
          successtip: "✓ HIGH FREQUENCY!\nUsually = 80-90% of the time 📈\nAlmost always, but not quite!"
        },
        {
          type: "order",
          q: "Arrange from MOST to LEAST frequent: never / always / occasionally / often",
          a: "always, often, occasionally, never",
          tip: "📊 FREQUENCY ORDER!\n\nFrom highest to lowest:\n100% = Always\n70% = Often\n50% = Occasionally  \n0% = Never\n\nKnowing this scale helps you express yourself precisely!\n\nYou can say exactly how often something happens! 🎯",
          successtip: "✓ SCALE MASTERED!\nAlways → Often → Occasionally → Never 📊\nFrequency order perfect!"
        },
        {
          type: "mc",
          q: "'Frequently' is closest in meaning to:",
          options: ["Often", "Rarely", "Never"],
          a: "Often",
          successtip: "✓ SYNONYM!\nFrequently ≈ Often (~70%) 🔄\nBoth mean 'many times'!"
        },
        {
          type: "blank",
          q: "If you want to say something happens 0% of the time, use the adverb: ___",
          a: "never",
          accept: { ignoreCase: true },
          successtip: "✓ ZERO FREQUENCY!\nNever = 0%, not at any time 🚫\nAbsolute negative!"
        },
        {
          type: "mc",
          q: "Which pair of adverbs has similar meaning?",
          options: [
            "Rarely and seldom",
            "Always and never",
            "Often and rarely"
          ],
          a: "Rarely and seldom",
          successtip: "✓ LOW FREQUENCY PAIR!\nRarely ≈ Seldom (~10-20%) 🔄\nBoth mean 'not very often'!"
        },
        {
          type: "mc",
          q: "Complete: 'I ___ go to that restaurant - maybe once or twice a year.' Most appropriate adverb:",
          options: ["rarely", "always", "usually"],
          a: "rarely",
          successtip: "✓ INFREQUENT!\nOnce or twice a year = rarely 📉\nLow frequency adverb!"
        },
        {
          type: "mc",
          q: "If someone says 'I occasionally work from home,' they mean:",
          options: [
            "Sometimes, but not very often",
            "Every single day",
            "Never"
          ],
          a: "Sometimes, but not very often",
          successtip: "✓ OCCASIONAL!\nOccasionally = some times, not regularly ⚖️\nMid-frequency, flexible!"
        }
      ]
    },

    // ========================================================================
    // SET 6: FREQUENCY VOCABULARY IN CONTEXT
    // ========================================================================
    {
      id: "m7-u2-s6",
      title: "Set 6: Using Frequency Adverbs Naturally",
      questions: [
        {
          type: "mc",
          q: "Choose the most logical frequency adverb: 'The sun ___ rises in the east.'",
          options: ["always", "sometimes", "rarely"],
          a: "always",
          successtip: "✓ NATURAL FACT!\nSun always rises in east 🌅\n100% = always!"
        },
        {
          type: "blank",
          q: "I ___ eat breakfast at home, maybe two or three times a week. (sometimes / occasionally)",
          a: "sometimes",
          accept: { ignoreCase: true, alts: ["occasionally"] },
          successtip: "✓ FLEXIBLE ROUTINE!\n2-3 times a week = sometimes 🥐\nMid-frequency fits!"
        },
        {
          type: "mc",
          q: "Complete naturally: 'She's a vegetarian, so she ___ eats meat.'",
          options: ["never", "always", "often"],
          a: "never",
          successtip: "✓ DIETARY CHOICE!\nVegetarian = never eats meat 🥗\n0% frequency!"
        },
        {
          type: "order",
          q: "Traduce: Nosotros usualmente cenamos juntos los domingos",
          a: "We usually have dinner together on Sundays",
          successtip: "✓ FAMILY TRADITION!\nUsually = regular Sunday routine 🍽️\nHigh frequency habit!"
        },
        {
          type: "mc",
          q: "'He ___ forgets his keys - I can only remember it happening twice.' Most accurate:",
          options: ["rarely", "always", "usually"],
          a: "rarely",
          tip: "🔑 PRECISE FREQUENCY!\n\n'Only twice' = very infrequent\n= RARELY (10-20%)\n\n'Always forgets' would mean every time ❌\n'Usually forgets' would mean most times ❌\n\nMatch the adverb to the actual frequency! 🎯",
          successtip: "✓ ACCURATE!\nOnly twice = rarely 🗝️\nLow frequency matches!"
        },
        {
          type: "mc",
          q: "Which sentence sounds most natural?",
          options: [
            "I'm always tired on Monday mornings",
            "I'm tired on Monday mornings always",
            "Always I'm tired on Monday mornings"
          ],
          a: "I'm always tired on Monday mornings",
          successtip: "✓ MONDAY MOOD!\nAm always tired = natural! 😴\nBE + adverb rule!"
        },
        {
          type: "blank",
          q: "My boss ___ works late - he's still in the office after 8 PM almost every night. (frequently / often)",
          a: "frequently",
          accept: { ignoreCase: true, alts: ["often"] },
          successtip: "✓ WORKAHOLIC!\nAlmost every night = frequently 💼\nHigh frequency!"
        },
        {
          type: "mc",
          q: "Complete with the most logical adverb: 'I ___ drink coffee after 6 PM because it keeps me awake.'",
          options: ["never", "always", "usually"],
          a: "never",
          successtip: "✓ SLEEP HABIT!\nAvoid coffee at night = never ☕🚫\n0% for sleep quality!"
        },
        {
          type: "mc",
          q: "Choose the natural completion: 'Traffic ___ terrible during rush hour.'",
          options: [
            "is usually",
            "usually is",
            "is usual"
          ],
          a: "is usually",
          successtip: "✓ TRAFFIC REALITY!\nIs usually terrible = common pattern 🚗\nBE + adverb exception!"
        },
        {
          type: "mc",
          q: "Final question: 'She ___ volunteers at the animal shelter on weekends.' If she does this 3 out of 4 weekends:",
          options: ["often", "rarely", "never"],
          a: "often",
          successtip: "✓ VOLUNTEERING!\n3 out of 4 = often/frequently 🐕❤️\n~75% = high frequency!"
        }
      ]
    },

    // ========================================================================
    // SET 7: FREQUENCY EXPRESSIONS AT END OF SENTENCE
    // ========================================================================
    {
      id: "m7-u2-s7",
      title: "Set 7: Frequency Expressions at End Position",
      questions: [
        {
          type: "mc",
          q: "Which frequency expression is correctly placed?",
          options: [
            "I exercise three times a week",
            "I three times a week exercise",
            "Three times a week I exercise"
          ],
          a: "I exercise three times a week",
          tip: "📍 END POSITION!\n\nSpecific frequency expressions go at the END:\n✅ 'I exercise THREE TIMES A WEEK'\n✅ 'We meet ONCE A MONTH'\n✅ 'She travels TWICE A YEAR'\n\nPattern: Subject + Verb + FREQUENCY EXPRESSION\n\nDifferent from adverbs like 'usually'! 🎯",
          successtip: "✓ END POSITION!\nVerb + three times a week = correct! 💪\nFrequency expression at end!"
        },
        {
          type: "blank",
          q: "We have team meetings ___. (once a week)",
          a: "once a week",
          accept: { ignoreCase: true, alts: ["once per week", "one time a week"] },
          successtip: "✓ REGULAR MEETINGS!\nMeetings + once a week = perfect! 📅\nEnd position correct!"
        },
        {
          type: "mc",
          q: "Choose the natural sentence:",
          options: [
            "She visits her parents twice a month",
            "She twice a month visits her parents",
            "Twice a month she visits her parents"
          ],
          a: "She visits her parents twice a month",
          successtip: "✓ FAMILY VISITS!\nVisits + twice a month = natural! 👨‍👩‍👧\nEnd position flows best!"
        },
        {
          type: "order",
          q: "Traduce: Ellos van al cine una vez al mes",
          a: "They go to the cinema once a month",
          successtip: "✓ MOVIE NIGHT!\nGo + once a month = perfect! 🎬\nFrequency at end!"
        },
        {
          type: "mc",
          q: "'I check my email ___' - which ending is correct?",
          options: [
            "several times a day",
            "several times daily",
            "Both are correct"
          ],
          a: "Both are correct",
          successtip: "✓ BOTH WORK!\nSeveral times a day/daily = both natural! 📧\nFlexible expressions!"
        },
        {
          type: "blank",
          q: "My family takes a vacation ___. (twice a year)",
          a: "twice a year",
          accept: { ignoreCase: true, alts: ["two times a year", "twice per year"] },
          successtip: "✓ VACATION PATTERN!\nTakes + twice a year = nice! ✈️\nEnd frequency expression!"
        },
        {
          type: "mc",
          q: "Which is the most natural way to express this?",
          options: [
            "I play tennis every Saturday",
            "Every Saturday I play tennis",
            "I every Saturday play tennis"
          ],
          a: "I play tennis every Saturday",
          tip: "🎾 'EVERY' EXPRESSIONS!\n\n'Every [day/week/month]' typically goes at the END:\n✅ 'I play tennis EVERY SATURDAY'\n\nAlso acceptable at beginning for emphasis:\n✅ 'Every Saturday, I play tennis'\n\nBut NEVER in the middle! ❌\n\nEnd position is most neutral and natural! 🎯",
          successtip: "✓ REGULAR SPORT!\nPlay tennis + every Saturday = perfect! 🎾\nEnd position most natural!"
        },
        {
          type: "mc",
          q: "Complete: 'She practices yoga ___.'",
          options: [
            "four times a week",
            "four times weekly",
            "Both are correct"
          ],
          a: "Both are correct",
          successtip: "✓ BOTH NATURAL!\nFour times a week/weekly = both work! 🧘\nFlexible frequency expressions!"
        },
        {
          type: "order",
          q: "Traduce: Yo leo el periódico todos los días",
          a: "I read the newspaper every day",
          successtip: "✓ DAILY HABIT!\nRead + every day = consistent! 📰\nFrequency at end!"
        },
        {
          type: "mc",
          q: "Which frequency expression is positioned correctly?",
          options: [
            "They travel abroad once or twice a year",
            "Once or twice a year they travel abroad",
            "They once or twice a year travel abroad"
          ],
          a: "They travel abroad once or twice a year",
          successtip: "✓ TRAVEL FREQUENCY!\nTravel + once or twice a year = perfect! 🌍\nEnd position most natural!"
        }
      ]
    },

    // ========================================================================
    // SET 8: MORE END-POSITION PRACTICE
    // ========================================================================
    {
      id: "m7-u2-s8",
      title: "Set 8: Mastering End-Position Frequency",
      questions: [
        {
          type: "mc",
          q: "Choose the most natural sentence:",
          options: [
            "I go to the dentist twice a year",
            "Twice a year I go to the dentist",
            "I twice a year go to the dentist"
          ],
          a: "I go to the dentist twice a year",
          successtip: "✓ DENTAL CHECKUP!\nGo + twice a year = natural! 🦷\nEnd position standard!"
        },
        {
          type: "blank",
          q: "We have lunch together ___. (once a week)",
          a: "once a week",
          accept: { ignoreCase: true, alts: ["once per week", "one time a week"] },
          successtip: "✓ LUNCH TRADITION!\nLunch + once a week = nice! 🍱\nEnd position perfect!"
        },
        {
          type: "mc",
          q: "'My gym membership allows me to attend classes ___.' Choose the natural completion:",
          options: [
            "up to five times a week",
            "up to five times weekly",
            "Both sound equally natural"
          ],
          a: "Both sound equally natural",
          successtip: "✓ GYM FLEXIBILITY!\nBoth expressions work! 🏋️\nFrequency expressions are flexible!"
        },
        {
          type: "order",
          q: "Traduce: Ella llama a su abuela cada domingo",
          a: "She calls her grandmother every Sunday",
          successtip: "✓ SWEET ROUTINE!\nCalls + every Sunday = lovely! 📞❤️\nEnd position natural!"
        },
        {
          type: "mc",
          q: "Which is correctly structured?",
          options: [
            "I update my resume once every few months",
            "Once every few months I update my resume",
            "Both are grammatically correct"
          ],
          a: "Both are grammatically correct",
          tip: "📝 FLEXIBLE PLACEMENT!\n\nBoth structures work:\n✅ End: 'I update my resume ONCE EVERY FEW MONTHS'\n✅ Beginning: 'Once every few months, I update my resume'\n\nEnd = neutral, most common\nBeginning = slight emphasis on frequency\n\nBoth grammatically correct! 🎯",
          successtip: "✓ BOTH CORRECT!\nEnd or beginning = both work! 📝\nEnd more common!"
        },
        {
          type: "blank",
          q: "The magazine is published ___. (monthly / once a month)",
          a: "monthly",
          accept: { ignoreCase: true, alts: ["once a month", "every month"] },
          successtip: "✓ PUBLICATION SCHEDULE!\nPublished + monthly = standard! 📖\nAll alternatives work!"
        },
        {
          type: "mc",
          q: "Complete naturally: 'I change my car's oil ___.'",
          options: [
            "every 5,000 kilometers",
            "5,000 kilometers every",
            "every I change 5,000 kilometers"
          ],
          a: "every 5,000 kilometers",
          successtip: "✓ CAR MAINTENANCE!\nChange + every 5,000 km = correct! 🚗\nEnd position clear!"
        },
        {
          type: "order",
          q: "Traduce: Nosotros reorganizamos la oficina cada tres meses",
          a: "We reorganize the office every three months",
          successtip: "✓ OFFICE REFRESH!\nReorganize + every three months = systematic! 🏢\nEnd frequency perfect!"
        },
        {
          type: "mc",
          q: "Which frequency expression fits best? 'He travels to Asia for business ___.'",
          options: [
            "three or four times a year",
            "frequently",
            "Both work, but first is more specific"
          ],
          a: "Both work, but first is more specific",
          tip: "🎯 SPECIFIC VS GENERAL!\n\n'Three or four times a year' = specific, precise\n'Frequently' = general, approximate\n\nBoth correct, different purposes:\n• Specific numbers = when exact frequency matters\n• Adverbs = when approximation is fine\n\nContext determines which is better! 🌏",
          successtip: "✓ PRECISION CHOICE!\nSpecific numbers = more precise ✈️\nBoth grammatically correct!"
        },
        {
          type: "mc",
          q: "Final question: 'I back up my computer files ___.' Most professional/precise:",
          options: [
            "once a week on Sundays",
            "weekly",
            "often"
          ],
          a: "once a week on Sundays",
          successtip: "✓ MOST SPECIFIC!\nOnce a week on Sundays = clear system! 💻\nPrecision shows good habits!"
        }
      ]
    },

    // ========================================================================
    // SET 9: INTEGRATION - AVOIDING COMMON ERRORS
    // ========================================================================
    {
      id: "m7-u2-s9",
      title: "Set 9: Common Errors & Integration",
      questions: [
        {
          type: "mc",
          q: "Identify the error: 'I walk always to work in the morning.'",
          options: [
            "'Walk always' should be 'always walk'",
            "'In the morning' is wrong position",
            "No error"
          ],
          a: "'Walk always' should be 'always walk'",
          tip: "🚫 COMMON ERROR!\n\n❌ 'Walk always' = WRONG\n✅ 'Always walk' = CORRECT\n\nAdverb must come BEFORE main verb!\n\nSubject + ADVERB + Verb\n\nThis is a very typical mistake learners make! 🎯",
          successtip: "✓ ERROR FIXED!\n'Walk always' → 'always walk' ✅\nAdverb before verb!"
        },
        {
          type: "mc",
          q: "Which sentence has INCORRECT adverb placement?",
          options: [
            "She eats rarely vegetables",
            "She rarely eats vegetables",
            "She is rarely hungry"
          ],
          a: "She eats rarely vegetables",
          successtip: "✓ ERROR SPOTTED!\n'Eats rarely' = wrong ❌\nShould be 'rarely eats' ✅"
        },
        {
          type: "blank",
          q: "Fix the error: 'My coffee often is cold by the time I drink it.'\n\nCorrect version: My coffee ___ cold by the time I drink it.",
          a: "is often",
          accept: { ignoreCase: true, alts: ["'s often"] },
          successtip: "✓ BE EXCEPTION!\n'Often is' → 'is often' ✅\nAdverb AFTER 'be'!"
        },
        {
          type: "order",
          q: "Traduce: Ellos nunca están satisfechos con los resultados",
          a: "They are never satisfied with the results",
          successtip: "✓ PERFECT WITH BE!\nAre + never = correct! 🎯\nBE exception applied!"
        },
        {
          type: "mc",
          q: "Find the error: 'I exercise four times weekly and I eat always healthy food.'",
          options: [
            "'Eat always' should be 'always eat'",
            "'Four times weekly' is wrong",
            "Both parts are wrong"
          ],
          a: "'Eat always' should be 'always eat'",
          successtip: "✓ ERROR FOUND!\n'Eat always' = incorrect ❌\n'Always eat' = correct ✅"
        },
        {
          type: "mc",
          q: "Which sentence integrates adverbs correctly throughout?",
          options: [
            "I usually wake up early, and I'm always at the office by 8 AM",
            "I wake up usually early, and I always am at the office by 8 AM",
            "I usually wake up early, and I always am at the office by 8 AM"
          ],
          a: "I usually wake up early, and I'm always at the office by 8 AM",
          tip: "✅ BOTH PATTERNS!\n\nFirst clause: 'I usually wake up' → adverb before verb ✅\nSecond clause: 'I'm always at' → adverb after BE ✅\n\nBoth rules applied correctly in one sentence!\n\nThis is natural, fluent English! 🌟",
          successtip: "✓ DOUBLE MASTERY!\nUsually wake (main) + am always (BE) 💼\nBoth patterns perfect!"
        },
        {
          type: "blank",
          q: "Fix all errors: 'The bus often is late and we miss sometimes our connection.'\n\nCorrect: The bus ___ late and we ___ our connection.",
          a: "is often, sometimes miss",
          accept: { ignoreCase: true, alts: ["is often / sometimes miss"] },
          successtip: "✓ BOTH FIXED!\nOften is → is often (BE) ✅\nMiss sometimes → sometimes miss (main) ✅"
        },
        {
          type: "mc",
          q: "Identify the error pattern: 'She goes frequently to the gym.'",
          options: [
            "Adverb should come before the verb, not after",
            "No error - this is correct",
            "Should use 'is frequently' instead"
          ],
          a: "Adverb should come before the verb, not after",
          successtip: "✓ ERROR IDENTIFIED!\n'Goes frequently' = wrong ❌\n'Frequently goes' = correct ✅"
        },
        {
          type: "order",
          q: "Traduce (sin errores): Yo usualmente estoy cansado pero siempre termino mi trabajo",
          a: "I am usually tired but I always finish my work",
          successtip: "✓ BOTH PATTERNS!\nAm usually (BE) + always finish (main) 💪\nIntegration perfect!"
        },
        {
          type: "mc",
          q: "FINAL ERROR CHECK: 'My schedule always is busy during the week, but I relax usually on weekends.' How many errors?",
          options: [
            "Two errors: 'always is' and 'relax usually'",
            "One error: only 'always is'",
            "No errors"
          ],
          a: "Two errors: 'always is' and 'relax usually'",
          tip: "🚫 DOUBLE ERROR!\n\nError 1: 'Always is' → should be 'is always' (BE exception)\nError 2: 'Relax usually' → should be 'usually relax' (main verb)\n\nCorrect version:\n'My schedule IS ALWAYS busy during the week, but I USUALLY RELAX on weekends.'\n\nBoth patterns in one sentence! 🎯",
          successtip: "✓ BOTH ERRORS FOUND!\n'Always is' → 'is always' ✅\n'Relax usually' → 'usually relax' ✅\nYou can spot errors!"
        }
      ]
    },

    // ========================================================================
    // SET 10: REAL-WORLD INTEGRATION & FLUENCY
    // ========================================================================
    {
      id: "m7-u2-s10",
      title: "Set 10: Natural Fluency - Routines & Free Time",
      questions: [
        {
          type: "mc",
          q: "Choose the most natural way to describe a morning routine:",
          options: [
            "I usually wake up at 6 AM, and my coffee is always ready by 6:15",
            "I wake up usually at 6 AM, and my coffee always is ready by 6:15",
            "Usually I wake up at 6 AM, and always my coffee is ready by 6:15"
          ],
          a: "I usually wake up at 6 AM, and my coffee is always ready by 6:15",
          successtip: "✓ NATURAL FLOW!\nUsually wake + is always = fluent! ☕⏰\nBoth patterns smooth!"
        },
        {
          type: "blank",
          q: "Complete naturally: 'On weekends, I ___ sleep late and I ___ breakfast in bed once in a while.' (rarely / have)",
          a: "rarely, have",
          accept: { ignoreCase: true, alts: ["rarely / have"] },
          successtip: "✓ WEEKEND LUXURY!\nRarely sleep + have once in a while 🛏️\nMixed frequency expressions!"
        },
        {
          type: "mc",
          q: "Which sentence sounds most natural when describing work-life balance?",
          options: [
            "I'm often stressed during the week, but I always make time for exercise",
            "I often am stressed during the week, but I make always time for exercise",
            "Often I'm stressed during the week, but always I make time for exercise"
          ],
          a: "I'm often stressed during the week, but I always make time for exercise",
          successtip: "✓ BALANCED LIFE!\nAm often + always make = natural! 💼💪\nWork-life balance expressed perfectly!"
        },
        {
          type: "order",
          q: "Traduce: Mi familia usualmente cena junta y nosotros nunca estamos con prisa",
          a: "My family usually has dinner together and we are never in a hurry",
          successtip: "✓ FAMILY TIME!\nUsually has + are never = beautiful! 🍽️❤️\nBoth patterns natural!"
        },
        {
          type: "mc",
          q: "Complete the leisure activity description: 'I ___ tennis on Saturdays, and the courts ___ available.'",
          options: [
            "usually play / are always",
            "play usually / always are",
            "usually play / always are"
          ],
          a: "usually play / are always",
          successtip: "✓ SPORTS ROUTINE!\nUsually play + are always = perfect! 🎾\nBoth patterns correct!"
        },
        {
          type: "mc",
          q: "Which best describes a flexible work schedule?",
          options: [
            "I sometimes work from home, but I'm rarely in the office after 6 PM",
            "I work sometimes from home, but I rarely am in the office after 6 PM",
            "Sometimes I work from home, but rarely I'm in the office after 6 PM"
          ],
          a: "I sometimes work from home, but I'm rarely in the office after 6 PM",
          tip: "💼 FLEXIBLE WORK!\n\nMost natural structure:\n'I sometimes work' (adverb before verb) ✅\n'I'm rarely in' (adverb after BE) ✅\n\nBoth patterns used correctly = fluent English!\n\nThis is how natives talk about work flexibility! 🌟",
          successtip: "✓ MODERN WORK!\nSometimes work + am rarely = natural! 💻🏠\nFlexible schedule described perfectly!"
        },
        {
          type: "blank",
          q: "Describe a social habit: 'We meet for coffee ___, and we ___ try a new café each time.' (once a week / usually)",
          a: "once a week, usually",
          accept: { ignoreCase: true, alts: ["once a week / usually"] },
          successtip: "✓ SOCIAL ROUTINE!\nOnce a week (end) + usually try (before verb) ☕👥\nBoth positions perfect!"
        },
        {
          type: "mc",
          q: "Choose the natural way to discuss reading habits:",
          options: [
            "I usually read before bed, and I'm always finished within 30 minutes",
            "I read usually before bed, and I always am finished within 30 minutes",
            "Usually I read before bed, and always I'm finished within 30 minutes"
          ],
          a: "I usually read before bed, and I'm always finished within 30 minutes",
          successtip: "✓ READING HABIT!\nUsually read + am always = natural! 📚😴\nEvening routine perfect!"
        },
        {
          type: "order",
          q: "Traduce: Los fines de semana son frecuentemente relajados y yo raramente pienso en el trabajo",
          a: "Weekends are frequently relaxed and I rarely think about work",
          successtip: "✓ WEEKEND PEACE!\nAre frequently + rarely think = balanced! 🏖️\nBoth patterns in harmony!"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: 'My daily routine ___ predictable, and I ___ the same breakfast every morning. On weekends, though, I ___ late and ___ different restaurants for brunch ___.' Fill all blanks correctly:",
          options: [
            "is usually / have always / sleep often / try always / twice a month",
            "is usually / always have / often sleep / always try / twice a month",
            "usually is / always have / sleep often / try always / twice a month"
          ],
          a: "is usually / always have / often sleep / always try / twice a month",
          tip: "🌟 COMPLETE INTEGRATION!\n\nBreaking it down:\n✅ 'Is usually' (BE + adverb)\n✅ 'Always have' (adverb + verb)\n✅ 'Often sleep' (adverb + verb)\n✅ 'Always try' (adverb + verb)\n✅ 'Twice a month' (end position)\n\nALL patterns used correctly!\n\n5 different frequency expressions in one paragraph! This is mastery! 🎓",
          successtip: "🎉 UNIT 2 MASTERED! 🎉\n\n✅ ADVERB PLACEMENT\nBefore main verbs ✅\nAfter verb 'be' ✅\n\n✅ FREQUENCY SCALE\nAlways → Never (100% → 0%) ✅\n\n✅ END EXPRESSIONS\nOnce a week, twice a month ✅\n\n✅ COMMON ERRORS\nYou can spot and fix them! ✅\n\n✅ NATURAL INTEGRATION\nAll patterns together fluently! ✅\n\n🌟 YOU CAN NOW:\n• Describe routines naturally\n• Use frequency precisely\n• Avoid common errors\n• Sound like a fluent speaker!\n\nThis is B1-B2 level mastery! 💪"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - MY WEEKLY ROUTINE (5 PASSAGES OF 100-120 WORDS)
  // ==========================================================================
  readingSet: {
    id: "m7-u2-reading",
    title: "Reading Practice - My Weekly Routine",
    category: "reading",
    
    questions: [
      // ======================================================================
      // BLOQUE 1: Morning Routine (100-120 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**My Weekly Routine**

**Morning Habits**

I'm definitely not a morning person, but I've developed a routine that works well for me. I usually wake up at 6:30 AM on weekdays, though I'm rarely happy about it! My alarm is always set for 6:15, but I frequently hit the snooze button once or twice.

My morning coffee is essential - I never skip it. I always prepare it first, even before I shower. The coffee maker is usually ready by 6:45, and I'm always grateful for that first cup. I occasionally check my phone while drinking my coffee, but I try not to start work emails too early.

By 7:15, I'm usually dressed and ready to leave. Traffic is often heavy, so I rarely arrive at the office before 8:00 AM.`
      },
      {
        type: "mc",
        q: "The text uses 'I'm rarely happy about it' when discussing waking up. Why is the adverb placed after 'am'?",
        options: [
          "Because 'be' is an exception - adverbs come after it",
          "It's a mistake - should be 'I rarely am happy'",
          "Both positions are equally correct"
        ],
        a: "Because 'be' is an exception - adverbs come after it",
        successtip: "✓ BE EXCEPTION SPOTTED!\n'Am rarely' = correct with BE ✅\nAdverb after verb 'be'!"
      },
      {
        type: "mc",
        q: "How often does the writer hit the snooze button?",
        options: [
          "Frequently - once or twice",
          "Never",
          "Always"
        ],
        a: "Frequently - once or twice",
        successtip: "✓ READING DETAIL!\n'Frequently hit snooze' = often but not always ⏰\nHigh frequency adverb!"
      },

      // ======================================================================
      // BLOQUE 2: Work Day (100-120 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Work Schedule**

My work schedule is usually consistent. I arrive at the office around 8:00 AM and always check my emails first thing. Meetings are frequently scheduled in the morning, which I personally prefer because I'm often more productive before lunch.

I rarely take a full hour for lunch - I usually eat at my desk while catching up on reading. My colleagues sometimes invite me to join them at restaurants, but I only go once or twice a week.

The afternoon is when I focus on projects. Deadlines are occasionally tight, so I sometimes stay late. However, I'm rarely in the office after 7:00 PM. I always try to maintain a good work-life balance.`
      },
      {
        type: "mc",
        q: "In the sentence 'I rarely take a full hour for lunch,' where is the adverb positioned?",
        options: [
          "Before the main verb 'take'",
          "After the main verb 'take'",
          "At the end of the sentence"
        ],
        a: "Before the main verb 'take'",
        successtip: "✓ CORRECT POSITION!\n'Rarely take' = adverb before verb ✅\nStandard placement with main verbs!"
      },
      {
        type: "blank",
        q: "How often does the writer go to restaurants with colleagues for lunch?",
        a: "once or twice a week",
        accept: { ignoreCase: true, alts: ["1 or 2 times a week", "once or twice weekly", "1-2 times a week"] },
        successtip: "✓ SPECIFIC FREQUENCY!\nOnce or twice a week = precise! 🍽️\nEnd-position expression!"
      },

      // ======================================================================
      // BLOQUE 3: Evening Routine (100-120 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Evening Activities**

My evenings are usually quite relaxed. I typically arrive home around 6:30 PM. I often cook dinner, though I occasionally order takeout when I'm too tired. The kitchen is rarely messy because I always clean up immediately after eating.

After dinner, I frequently watch a show or read for an hour. I'm never productive with work in the evening - my brain is always too tired by then! I sometimes call my family around 8:00 PM, especially on weekdays when everyone is usually available.

My bedtime routine is fairly consistent. I'm always in bed by 10:30 PM on weeknights. I rarely stay up past 11:00 PM because I know I'll regret it in the morning!`
      },
      {
        type: "mc",
        q: "The writer says 'I'm always in bed by 10:30 PM.' Why is 'always' after 'am'?",
        options: [
          "It's the BE verb exception - adverbs go after 'be'",
          "It's an error",
          "For emphasis only"
        ],
        a: "It's the BE verb exception - adverbs go after 'be'",
        successtip: "✓ BE PATTERN!\n'Am always' = correct with BE ✅\nException rule in context!"
      },
      {
        type: "mc",
        q: "How often does the kitchen get messy?",
        options: [
          "Rarely - because cleaning is done immediately",
          "Frequently",
          "Always"
        ],
        a: "Rarely - because cleaning is done immediately",
        successtip: "✓ CLEAN HABIT!\n'Rarely messy' + 'always clean up' = good system! 🧹\nLow frequency explained!"
      },

      // ======================================================================
      // BLOQUE 4: Weekend Schedule (100-120 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Weekend Differences**

My weekend routine is completely different. I never set an alarm on Saturdays - I usually wake up naturally around 8:30 or 9:00 AM. Breakfast is always more elaborate on weekends. I frequently make pancakes or eggs, which I rarely have time for during the week.

Saturday afternoons are often spent outdoors. I usually go hiking or cycling, weather permitting. I occasionally meet friends for lunch, typically at a new restaurant we want to try.

Sunday mornings are sacred - I always reserve them for a long, slow breakfast and reading the news. The afternoons are usually for errands and meal prep. I'm frequently at the grocery store on Sunday afternoons, along with everyone else in the neighborhood!`
      },
      {
        type: "mc",
        q: "The writer says 'I never set an alarm.' What does this adverb placement show?",
        options: [
          "Correct placement - adverb before main verb 'set'",
          "Incorrect - should be 'I set never'",
          "Should be 'I am never setting'"
        ],
        a: "Correct placement - adverb before main verb 'set'",
        successtip: "✓ NEVER PLACED RIGHT!\n'Never set' = correct! ⏰🚫\nAdverb before main verb!"
      },
      {
        type: "blank",
        q: "According to the text, when are Sunday mornings reserved for?",
        a: "breakfast and reading",
        accept: { ignoreCase: true, alts: ["long breakfast and reading", "slow breakfast and reading the news", "breakfast and reading the news"] },
        successtip: "✓ SACRED TIME!\nSunday mornings = 'always' reserved 📰☕\nConsistent weekend habit!"
      },

      // ======================================================================
      // BLOQUE 5: Reflection (100-120 words) + FINAL QUESTION
      // ======================================================================
      {
        type: "passage",
        passage: `**Maintaining Balance**

Looking at my weekly routine, I realize I'm fairly consistent with my habits. Some patterns are definitely healthier than others! I frequently tell myself I should exercise more - I currently work out only twice a week, which is rarely enough.

I'm usually good about maintaining work-life balance during the week, though deadlines occasionally disrupt that. I never check work emails on Sunday, which is a rule I always keep.

Overall, my routine is probably similar to many professionals. We're often too busy during the week and occasionally too relaxed on weekends! But I rarely feel overwhelmed, which suggests my system is working reasonably well. The key is finding what patterns work for you and sticking to them consistently.`
      },
      {
        type: "mc",
        q: "In 'I'm usually good about maintaining balance,' why is 'usually' after 'am'?",
        options: [
          "BE verb exception - adverbs follow 'be'",
          "It's incorrect placement",
          "No particular reason"
        ],
        a: "BE verb exception - adverbs follow 'be'",
        successtip: "✓ BE PATTERN AGAIN!\n'Am usually' = correct! ⚖️\nException rule consistent throughout text!"
      },
      {
        type: "mc",
        q: "How often does the writer currently exercise?",
        options: [
          "Twice a week",
          "Rarely",
          "Frequently"
        ],
        a: "Twice a week",
        successtip: "✓ SPECIFIC FREQUENCY!\n'Twice a week' = end-position expression! 💪\nExact frequency stated!"
      },
      {
        type: "mc",
        q: "This reading demonstrates all the Unit 2 concepts. How many different frequency patterns can you identify?",
        options: [
          "All major patterns: adverbs before verbs, adverbs after 'be', and end-position expressions",
          "Only one pattern used throughout",
          "Inconsistent and confusing patterns"
        ],
        a: "All major patterns: adverbs before verbs, adverbs after 'be', and end-position expressions",
        tip: "🎯 COMPLETE INTEGRATION!\n\nThis text naturally includes:\n\n✅ Adverbs BEFORE main verbs:\n'I usually wake up', 'I never skip', 'I rarely take'\n\n✅ Adverbs AFTER 'be':\n'I'm rarely happy', 'I'm always in bed', 'I'm usually good'\n\n✅ End-position expressions:\n'once or twice a week', 'twice a week', 'by 10:30 PM'\n\n✅ Full frequency scale:\nAlways, usually, often, frequently, occasionally, sometimes, rarely, never\n\nALL patterns used naturally in context! This is authentic English! 🌟",
        successtip: "🎉 READING COMPLETE! 🎉\n\n📚 YOU RECOGNIZED:\n\n✅ Adverb + Verb patterns\n'Usually wake', 'never skip', 'rarely take'\n\n✅ BE + Adverb patterns\n'Am rarely', 'is usually', 'are frequently'\n\n✅ End-position expressions\n'Twice a week', 'once or twice', 'by 10:30 PM'\n\n✅ Full frequency vocabulary\nAlways → Never (complete scale!)\n\n✅ Natural, authentic English\nReal-world routine description!\n\n🌟 YOU CAN NOW:\n• Understand frequency in context ✅\n• Recognize all adverb patterns ✅\n• Read about routines naturally ✅\n\nAuthentic B1-B2 reading mastered! 💪📖"
      }
    ]
  },

   // ==========================================================================
  // REVIEW SET - UNIT 2 INTEGRATION (COMPLETADO)
  // ==========================================================================
  reviewSet: {
    id: "m7-u2-review",
    title: "⭐ Unit 2 Review: Routine and Free Time",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "Which sentence has CORRECT adverb placement?",
        options: [
          "She always arrives early for meetings",
          "She arrives always early for meetings",
          "Always she arrives early for meetings"
        ],
        a: "She always arrives early for meetings",
        tip: "📍 REVIEW: ADVERB POSITION!\n\nWith main verbs: Subject + ADVERB + Verb\n✅ 'She always arrives'\n\nWith BE verb: Subject + BE + ADVERB\n✅ 'She is always early'\n\nDifferent verbs, different rules! 🎯",
        successtip: "✓ CORRECT PLACEMENT!\nAlways + arrives = perfect! ✅\nAdverb before main verb!"
      },
      
      {
        type: "mc",
        q: "Choose the correct sentence with verb 'be':",
        options: [
          "The coffee is usually cold by lunchtime",
          "The coffee usually is cold by lunchtime",
          "Usually the coffee is cold by lunchtime"
        ],
        a: "The coffee is usually cold by lunchtime",
        successtip: "✓ BE EXCEPTION!\nIs + usually = correct! ☕\nAdverb AFTER 'be'!"
      },
      
      {
        type: "blank",
        q: "Complete both blanks correctly: 'I ___ tired in the morning, but I ___ finish my work on time.' (am always / rarely)\n\nAnswer: I ___ tired, but I ___ finish",
        a: "am always, rarely",
        accept: { ignoreCase: true, alts: ["am always / rarely", "'m always, rarely", "'m always / rarely"] },
        successtip: "✓ BOTH PATTERNS!\nAm always (BE) + rarely finish (main) 🎯\nBoth rules applied!"
      },
      
      {
        type: "mc",
        q: "Which frequency adverb means approximately 0%?",
        options: ["Never", "Rarely", "Sometimes"],
        a: "Never",
        successtip: "✓ FREQUENCY SCALE!\nNever = 0%, not at any time 🚫\nAbsolute zero frequency!"
      },
      
      {
        type: "order",
        q: "Traduce: Nosotros visitamos a nuestros abuelos dos veces al mes",
        a: "We visit our grandparents twice a month",
        successtip: "✓ END EXPRESSION!\nVisit + twice a month = perfect! 👵👴\nFrequency at end position!"
      },
      
      {
        type: "mc",
        q: "Identify ALL errors: 'She goes often to the gym and she eats always healthy food.'",
        options: [
          "Both 'goes often' and 'eats always' are wrong",
          "Only 'goes often' is wrong",
          "Only 'eats always' is wrong"
        ],
        a: "Both 'goes often' and 'eats always' are wrong",
        successtip: "✓ BOTH ERRORS!\n'Goes often' → 'often goes' ✅\n'Eats always' → 'always eats' ✅"
      },
      
      {
        type: "blank",
        q: "Which adverb fits? 'I exercise ___' if you go to the gym 3 times per week. (frequently / often / regularly)",
        a: "frequently",
        accept: { ignoreCase: true, alts: ["often", "regularly", "three times a week"] },
        successtip: "✓ HIGH FREQUENCY!\n3 times a week = frequently/often! 💪\nMultiple correct answers!"
      },
      
      {
        type: "mc",
        q: "Which sentence correctly uses BOTH adverb patterns?",
        options: [
          "Traffic is often bad, so I usually leave early",
          "Traffic often is bad, so I leave usually early",
          "Often traffic is bad, so usually I leave early"
        ],
        a: "Traffic is often bad, so I usually leave early",
        successtip: "✓ INTEGRATION!\nIs often (BE) + usually leave (main) 🚗\nBoth patterns perfect!"
      },
      
      {
        type: "order",
        q: "Traduce: Ella nunca está estresada pero frecuentemente trabaja horas extras",
        a: "She is never stressed but she frequently works overtime",
        successtip: "✓ CONTRAST!\nIs never (BE) + frequently works (main) 💼\nBoth rules in one sentence!"
      },
      
      {
        type: "mc",
        q: "FINAL MASTERY QUESTION: 'My morning routine ___ consistent. I ___ wake up at 6 AM and I ___ my coffee first. I ___ to work by 7:30 and meetings ___ at 8. I work out ___ to stay healthy.' Fill ALL blanks correctly:",
        options: [
          "is usually / always / always have / usually drive / are often / three times a week",
          "usually is / always / have always / drive usually / often are / three times a week",
          "is usually / always / always have / usually drive / often are / three times a week"
        ],
        a: "is usually / always / always have / usually drive / are often / three times a week",
        tip: "🌟 COMPLETE UNIT 2 MASTERY!\n\nBreaking down ALL patterns:\n\n✅ 'Is usually' (BE + adverb)\n✅ 'Always wake up' (adverb + verb)\n✅ 'Always have' (adverb + verb)\n✅ 'Usually drive' (adverb + verb)\n✅ 'Are often' (BE + adverb)\n✅ 'Three times a week' (end position)\n\nSIX different frequency patterns in one paragraph!\n\nAll three rules applied multiple times:\n• Adverbs before main verbs ✅\n• Adverbs after 'be' ✅\n• Specific expressions at end ✅\n\nThis is complete mastery! 🎓",
        successtip: "🎉 UNIT 2 COMPLETELY MASTERED! 🎉\n\n✅ ADVERB PLACEMENT\n• Before main verbs (always wake) ✅\n• After BE verb (is usually, are often) ✅\n• End expressions (three times a week) ✅\n\n✅ FREQUENCY VOCABULARY\n• Always, usually, often, frequently ✅\n• Sometimes, occasionally, rarely, never ✅\n• Full 0%-100% scale mastered! ✅\n\n✅ COMMON ERRORS\n• Can spot 'goes often' error ✅\n• Can spot 'often is' error ✅\n• Can fix both types! ✅\n\n✅ NATURAL INTEGRATION\n• Multiple patterns in one sentence ✅\n• Authentic routine descriptions ✅\n• Fluent frequency expressions ✅\n\n🌟 YOU CAN NOW:\n• Place adverbs correctly 100% ✅\n• Use frequency scale precisely ✅\n• Avoid common errors ✅\n• Describe routines like a native! ✅\n\nB1-B2 frequency mastery complete! 💪\n\n¡Excelente trabajo! Ready for Unit 3! 🚀"
      }
    ]
  }
};
